import {
  isMasterMemberAgent,
  MASTER_MEMBER_MARKUP_RATE,
} from "./hotelMasterMember";
import { isRatePlanExplicitlyPanOptional } from "./hotelMealTypes";

export const HOTEL_PORTAL_DISPLAY_DISCOUNT_RATE_NEAR = 0.005;
export const HOTEL_PORTAL_DISPLAY_DISCOUNT_RATE_FAR = 0.01;
export const HOTEL_PORTAL_NEAR_BOOKING_DAYS = 45;
/** PAN-optional ("Without PAN") room rates are sold at API gross +2%. */
export const HOTEL_WITHOUT_PAN_MARKUP_RATE = 0.02;
/** Guest (RoleType = 4 / without login) hotel search + details: API gross +2%. */
export const HOTEL_GUEST_LISTING_MARKUP_RATE = 0.02;

/** @deprecated Use resolvePortalDiscountRate(checkInDate). */
export const HOTEL_PORTAL_DISPLAY_DISCOUNT_RATE =
  HOTEL_PORTAL_DISPLAY_DISCOUNT_RATE_NEAR;

const startOfLocalDay = (value) => {
  const d = new Date(value);
  d.setHours(0, 0, 0, 0);
  return d;
};

const parseCheckInDate = (value) => {
  if (value == null || value === "") {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : startOfLocalDay(value);
  }
  const raw = String(value).trim();
  if (!raw) {
    return null;
  }
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const d = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    return Number.isNaN(d.getTime()) ? null : startOfLocalDay(d);
  }
  const dmy = raw.match(/^(\d{2})-(\d{2})-(\d{4})/);
  if (dmy) {
    const d = new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]));
    return Number.isNaN(d.getTime()) ? null : startOfLocalDay(d);
  }
  const dmySlash = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (dmySlash) {
    const d = new Date(
      Number(dmySlash[3]),
      Number(dmySlash[2]) - 1,
      Number(dmySlash[1]),
    );
    return Number.isNaN(d.getTime()) ? null : startOfLocalDay(d);
  }
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : startOfLocalDay(d);
};

export const resolvePortalDiscountRate = (checkInDate) => {
  const checkIn = parseCheckInDate(checkInDate);
  if (!checkIn) {
    return HOTEL_PORTAL_DISPLAY_DISCOUNT_RATE_NEAR;
  }
  const today = startOfLocalDay(new Date());
  const msPerDay = 24 * 60 * 60 * 1000;
  const daysUntilCheckIn = Math.floor(
    (checkIn.getTime() - today.getTime()) / msPerDay,
  );
  return daysUntilCheckIn <= HOTEL_PORTAL_NEAR_BOOKING_DAYS
    ? HOTEL_PORTAL_DISPLAY_DISCOUNT_RATE_NEAR
    : HOTEL_PORTAL_DISPLAY_DISCOUNT_RATE_FAR;
};

export const applyHotelPortalDisplayDiscountFromGross = (gross, checkInDate, options = {}) => {
  const g = Math.ceil(Number(gross) || 0);
  if (g <= 0) {
    return g;
  }
  const markupRate =
    Number(options?.markupRate) > 0
      ? Number(options.markupRate)
      : isMasterMemberAgent(options?.roleType, options?.membership)
        ? MASTER_MEMBER_MARKUP_RATE
        : 0;
  if (markupRate > 0) {
    return Math.ceil(g * (1 + markupRate));
  }
  const rate =
    Number(options?.discountRate) > 0 ? Number(options.discountRate) : 0;
  const discounted = Math.floor(g * (1 - rate));
  if (rate > 0 && discounted >= g) {
    return Math.max(0, g - 1);
  }
  return discounted;
};

export const applyHotelPortalDisplayDiscount = (amount, checkInDate) =>
  applyHotelPortalDisplayDiscountFromGross(amount, checkInDate);

/** Locked supplier/API stay total on listing rows (before portal discount). */
export const readEffectiveListingMinPrice = (hotel = {}) =>
  Math.ceil(
    Number(
      hotel?.hotelMinPrice ??
        hotel?.HotelMinPrice ??
        hotel?.supplierApiFare ??
        hotel?.supplierGrossPrice ??
        hotel?.supplierListPrice ??
        0,
    ),
  );

/** Pre-discount supplier/API stay total locked by Hotels service on listing rows. */
export const resolveHotelListingSupplierGrossTotal = (hotel = {}, checkInDate) => {
  const locked = Math.ceil(
    Number(
      hotel?.supplierApiFare ??
        hotel?.supplierGrossPrice ??
        hotel?.supplierListPrice ??
        0,
    ),
  );
  if (locked > 0) {
    return locked;
  }
  const display = Math.ceil(Number(hotel?.hotelMinPrice ?? hotel?.HotelMinPrice ?? 0));
  if (display <= 0) {
    return 0;
  }
  const markupRate = Number(hotel?.portalMarkupRate ?? 0);
  if (markupRate > 0) {
    return Math.max(display, Math.ceil(display / (1 + markupRate)));
  }
  const discountRate =
    Number(hotel?.portalDiscountRate) > 0 ? Number(hotel.portalDiscountRate) : 0;
  if (discountRate > 0 && discountRate < 1) {
    return Math.max(display, Math.ceil(display / (1 - discountRate)));
  }
  return 0;
};

/** City discount, guest 2%, Master 12%, and Without-PAN 2% are Tripjack 3.0 only. */
export const hotelUsesTripjackV3PortalRules = (hotel = {}) =>
  hotel?.portalRulesApply === true ||
  String(hotel?.tripjackApiVersion ?? "").toLowerCase() === "v3";

/** Listing card — trust Hotels API price when portal rules already applied. */
export const resolveHotelListingDisplayPrice = (
  hotel = {},
  checkInDate,
  { roleType, membership } = {},
) => {
  const apiDisplay = readEffectiveListingMinPrice(hotel);
  if (!hotelUsesTripjackV3PortalRules(hotel)) {
    return apiDisplay;
  }
  if (apiDisplay > 0 && hotel?.portalPriceApplied === true) {
    return apiDisplay;
  }

  const priceOptions = { roleType, membership };
  const supplierGross = Math.ceil(
    Number(
      hotel?.supplierApiFare ??
        hotel?.supplierGrossPrice ??
        hotel?.supplierListPrice ??
        0,
    ),
  );
  let gross = supplierGross > 0 ? supplierGross : apiDisplay;
  if (gross <= 0) {
    return 0;
  }

  const effectiveRoleType = roleType ?? 4;
  if (Number(effectiveRoleType) === 4) {
    return Math.ceil(gross * (1 + HOTEL_GUEST_LISTING_MARKUP_RATE));
  }

  const discounted = applyHotelPortalDisplayDiscountFromGross(
    gross,
    checkInDate,
    priceOptions,
  );
  if (isMasterMemberAgent(roleType, membership)) {
    return discounted;
  }
  return discounted > 0 ? discounted : apiDisplay > 0 ? apiDisplay : gross;
};

/** Single entry for HotelsList card total — never trust hotelMinPrice=0 when gross exists. */
export const getHotelListCardDisplayPrice = (
  hotel = {},
  checkInDate,
  { roleType, membership } = {},
) => {
  const apiDisplay = Math.ceil(
    Number(hotel?.hotelMinPrice ?? hotel?.HotelMinPrice ?? 0),
  );
  if (apiDisplay > 0 && hotel?.portalPriceApplied === true) {
    return apiDisplay;
  }
  const resolved = resolveHotelListingDisplayPrice(hotel, checkInDate, {
    roleType,
    membership,
  });
  if (resolved > 0) {
    return resolved;
  }
  const gross = Math.ceil(
    Number(
      hotel?.supplierApiFare ??
        hotel?.supplierGrossPrice ??
        hotel?.supplierListPrice ??
        0,
    ),
  );
  return gross > 0 ? gross : 0;
};

const captureRatePlanSupplierGross = (price = {}, checkInDate) => {
  const tripjackGross = Math.ceil(Number(price?.tripjackSupplierGross ?? 0));
  if (tripjackGross > 0) {
    return tripjackGross;
  }
  const lockedGross = Math.ceil(Number(price?.supplierGross ?? 0));
  if (price?.tripjackGrossLocked === true && lockedGross > 0) {
    return lockedGross;
  }
  const displayTotal = Math.ceil(Number(price?.total ?? price?.Total ?? 0));
  const storedGross = Math.ceil(Number(price?.supplierGross ?? 0));
  const wasApplied = price?.portalPriceApplied === true;

  if (!wasApplied) {
    const storedGrossBeforeNet = Math.ceil(Number(price?.supplierGross ?? 0));
    if (storedGrossBeforeNet > 0) {
      return storedGrossBeforeNet;
    }
    const raw = Math.ceil(
      Number(
        price?.supplierNet ??
          price?.SupplierNet ??
          price?.offerPrice ??
          price?.OfferPrice ??
          displayTotal ??
          0,
      ),
    );
    if (raw > 0) {
      return raw;
    }
  }

  if (storedGross > 0) {
    if (displayTotal > 0 && displayTotal < storedGross && !wasApplied) {
      return displayTotal;
    }
    return storedGross;
  }

  if (displayTotal > 0 && wasApplied) {
    const markupRate = Number(price?.portalMarkupRate) || 0;
    if (markupRate > 0) {
      return Math.max(displayTotal, Math.ceil(displayTotal / (1 + markupRate)));
    }
    // Only reverse-inflate when a real portal discount was recorded.
    // portalPriceApplied + portalDiscountRate=0 means total is still supplier/API gross.
    const oldRate = Number(price?.portalDiscountRate) || 0;
    if (oldRate > 0 && oldRate < 1) {
      return Math.max(displayTotal, Math.ceil(displayTotal / (1 - oldRate)));
    }
    return displayTotal;
  }

  return Math.ceil(
    Number(
      price?.supplierNet ??
        price?.SupplierNet ??
        price?.offerPrice ??
        price?.OfferPrice ??
        displayTotal ??
        0,
    ),
  );
};

/** Apply portal discount or Master-member markup to a single rate-plan price row. */
export const applyPortalOrMasterDisplayPricingToRatePlanPrice = (
  price = {},
  { checkInDate, roleType, membership, markupRate: requestedMarkupRate, forcePricing = false } = {},
) => {
  if (
    !price ||
    typeof price !== "object" ||
    (price.customerDisplayPinned === true && !forcePricing)
  ) {
    return price;
  }
  // Hotels API already applied portal discount/markup — keep UI total unless forcing Without-PAN refresh.
  if (price.portalPriceApplied === true && !forcePricing) {
    return price;
  }
  const gross = captureRatePlanSupplierGross(price, checkInDate);
  if (gross <= 0) {
    return price;
  }
  const roleMarkupRate = isMasterMemberAgent(roleType, membership)
    ? MASTER_MEMBER_MARKUP_RATE
    : 0;
  const markupRate = Math.max(
    roleMarkupRate,
    Number(requestedMarkupRate) || 0,
  );
  const displayTotal = applyHotelPortalDisplayDiscountFromGross(gross, checkInDate, {
    roleType,
    membership,
    markupRate,
    discountRate: markupRate > 0 ? 0 : undefined,
  });
  const tax = Math.max(0, Number(price.tax ?? price.Tax ?? 0));
  const next = { ...price };
  next.supplierGross = gross;
  next.tripjackSupplierGross = gross;
  next.tripjackGrossLocked = true;
  next.total = displayTotal;
  next.Total = displayTotal;
  next.offerPrice = displayTotal;
  next.supplierNet = displayTotal;
  next.base = tax > 0 && displayTotal > tax ? displayTotal - tax : displayTotal;
  next.tax = tax > 0 && displayTotal > tax ? tax : 0;
  next.portalPriceApplied = true;
  next.portalDiscountRate = markupRate > 0 ? 0 : Number(price?.portalDiscountRate) || 0;
  if (markupRate > 0) {
    next.portalMarkupRate = markupRate;
  }
  delete next.adminHotelMarkupApplied;
  return next;
};

/** Admin Hotel Commission markup, added once onto the room the guest sees. */
export const applyAdminHotelMarkupToRatePlanPrice = (price = {}) => {
  if (!price || typeof price !== "object") {
    return price;
  }
  if (price.adminHotelMarkupApplied === true) {
    return price;
  }
  const markup = Math.max(0, Number(price.markup ?? price.Markup ?? 0));
  const total = Number(price.total ?? price.Total ?? 0);
  if (!(markup > 0) || !(total > 0)) {
    return { ...price, adminHotelMarkupApplied: true };
  }
  const nextTotal = Number((total + markup).toFixed(2));
  return {
    ...price,
    total: nextTotal,
    Total: nextTotal,
    offerPrice: nextTotal,
    markup,
    adminHotelMarkupApplied: true,
  };
};

export const applyAdminHotelMarkupToRooms = (rooms = []) => {
  if (!Array.isArray(rooms) || rooms.length === 0) {
    return rooms;
  }
  return rooms.map((room) => {
    const nestedRooms = Array.isArray(room?.rooms)
      ? applyAdminHotelMarkupToRooms(room.rooms)
      : room?.rooms;
    const ratePlans = Array.isArray(room?.ratePlans)
      ? room.ratePlans.map((ratePlan) => ({
          ...ratePlan,
          price: applyAdminHotelMarkupToRatePlanPrice(ratePlan?.price),
        }))
      : room?.ratePlans;
    return {
      ...room,
      ...(nestedRooms !== room?.rooms ? { rooms: nestedRooms } : {}),
      ...(ratePlans !== room?.ratePlans ? { ratePlans } : {}),
    };
  });
};

/** Apply portal, Master-member, and Without-PAN pricing to open/fixed room cards. */
export const applyPortalOrMasterDisplayPricingToRooms = (
  rooms = [],
  { checkInDate, roleType, membership, markupRate: requestedMarkupRate, portalRulesApply = true } = {},
) => {
  if (!Array.isArray(rooms) || rooms.length === 0 || portalRulesApply !== true) {
    return rooms;
  }
  return rooms.map((room) => {
    const nestedRooms = Array.isArray(room?.rooms)
      ? applyPortalOrMasterDisplayPricingToRooms(room.rooms, {
          checkInDate,
          roleType,
          membership,
          markupRate: requestedMarkupRate,
          portalRulesApply,
        })
      : room?.rooms;
    const ratePlans = Array.isArray(room?.ratePlans) ? [...room.ratePlans] : [];
    if (ratePlans.length === 0) {
      return nestedRooms === room?.rooms ? room : { ...room, rooms: nestedRooms };
    }
    const priced = ratePlans.map((rp) => {
      const roleMarkupRate = isMasterMemberAgent(roleType, membership)
        ? MASTER_MEMBER_MARKUP_RATE
        : 0;
      const explicitlyOptional =
        roleMarkupRate <= 0 &&
        isRatePlanExplicitlyPanOptional(rp, room) &&
        rp?.price?.panOptionalMarkupApplied !== true;
      const effectiveRoleType = Number(roleType ?? 4);
      const guestMarkupRate =
        effectiveRoleType === 4 ? HOTEL_GUEST_LISTING_MARKUP_RATE : 0;
      if (
        explicitlyOptional &&
        guestMarkupRate <= 0 &&
        rp?.price?.portalPriceApplied === true
      ) {
        const current = Math.ceil(Number(rp.price.total ?? rp.price.Total ?? 0));
        const withPanMarkup =
          current > 0
            ? Math.ceil(current * (1 + HOTEL_WITHOUT_PAN_MARKUP_RATE))
            : current;
        return {
          ...rp,
          price: {
            ...rp.price,
            total: withPanMarkup,
            Total: withPanMarkup,
            offerPrice: withPanMarkup,
            panOptionalMarkupApplied: true,
            panOptionalMarkupRate: HOTEL_WITHOUT_PAN_MARKUP_RATE,
          },
        };
      }
      const markupRate = Math.max(
        roleMarkupRate,
        explicitlyOptional ? HOTEL_WITHOUT_PAN_MARKUP_RATE : 0,
        Number(requestedMarkupRate) || 0,
        guestMarkupRate,
      );
      const hasPortalMarkup = Number(rp?.price?.portalMarkupRate) > 0;
      return {
        ...rp,
        price: applyPortalOrMasterDisplayPricingToRatePlanPrice(rp?.price, {
          checkInDate,
          roleType,
          membership,
          markupRate,
          forcePricing:
            explicitlyOptional ||
            roleMarkupRate > 0 ||
            Number(requestedMarkupRate) > 0 ||
            guestMarkupRate > 0 ||
            (hasPortalMarkup && roleMarkupRate > 0),
        }),
      };
    });
    return {
      ...room,
      ...(nestedRooms !== room?.rooms ? { rooms: nestedRooms } : {}),
      ratePlans: priced,
    };
  });
};

/** Checkout: keep selected Without-PAN/Master display totals through review and payment. */
export const resolveCheckoutHotelsWithDisplayPricing = (
  hotels = {},
  roomPlan = [],
  { checkInDate, roleType, membership } = {},
) => {
  if (!hotels?.rooms) {
    return { hotels, roomPlan };
  }
  const markedRooms = applyAdminHotelMarkupToRooms(
    applyPortalOrMasterDisplayPricingToRooms(hotels.rooms, {
      checkInDate,
      roleType,
      membership,
    }),
  );
  const nextHotels = { ...hotels, rooms: markedRooms };

  const nextPlan = (Array.isArray(roomPlan) ? roomPlan : []).map((plan, index) => {
    const pinnedDisplay = Math.ceil(
      Number(plan?.displayTotal ?? plan?.DisplayTotal ?? 0),
    );
    if (pinnedDisplay > 0) {
      return plan;
    }
    const room = markedRooms[index] ?? markedRooms[0];
    const ratePlan = room?.ratePlans?.[0];
    const planRateId = String(plan?.rateID ?? plan?.rateId ?? '').trim();
    const returnedRateId = String(ratePlan?.ratePlanId ?? '').trim();
    if (planRateId && returnedRateId && planRateId !== returnedRateId) {
      return plan;
    }
    const explicitlyOptional = isRatePlanExplicitlyPanOptional(ratePlan, room);
    const shouldRefreshDisplayTotal =
      isMasterMemberAgent(roleType, membership) || explicitlyOptional;
    if (!shouldRefreshDisplayTotal) {
      return plan;
    }
    const displayTotal = Math.ceil(
      Number(ratePlan?.price?.total ?? 0),
    );
    if (displayTotal <= 0) {
      return plan;
    }
    return { ...plan, displayTotal };
  });

  return { hotels: nextHotels, roomPlan: nextPlan };
};

/** TDS % on agent commission at checkout/review (matches Hotels backend TDS env default). */
export const HOTEL_CHECKOUT_TDS_PERCENT = 2;

/**
 * Agent checkout/review fare display:
 * - Final Payment = room line total (API total per room)
 * - Commission = DB % applied on that total (rate inferred from API base commission)
 * - TDS = TDS% of commission (rounded)
 * - Taxes = API tax + other charges (rounded)
 * - Room Price = Final Payment − Commission − TDS − Taxes
 */
export const getHotelAgentCheckoutFareDisplay = ({
  roomLineTotal = 0,
  apiBaseFare = 0,
  apiTax = 0,
  apiOtherCharges = 0,
  apiCommission = 0,
  apiTds = 0,
  tdsPercent = HOTEL_CHECKOUT_TDS_PERCENT,
}) => {
  const lineTotal = Math.max(0, Math.round(Number(roomLineTotal) || 0));
  const taxes = Math.max(
    0,
    Math.round(Number(apiTax) + Number(apiOtherCharges)),
  );
  const grossCommissionFromApi = Math.max(
    0,
    Number(apiCommission) + Number(apiTds),
  );
  const baseFare = Math.max(0, Number(apiBaseFare));

  const commissionRate =
    baseFare > 0 ? grossCommissionFromApi / baseFare : 0;
  const commission = Math.max(0, Math.round(lineTotal * commissionRate));
  const tds = Math.max(
    0,
    Math.round((commission * Number(tdsPercent)) / 100),
  );
  const roomPrice = Math.max(0, lineTotal - commission - tds - taxes);

  return {
    finalPayment: lineTotal,
    roomPrice,
    taxes,
    commission,
    tds,
  };
};

/** Checkout/review total: backend already sends discounted room totals — do not discount again. */
export const getHotelDisplayTotalAmount = ({
  hotelFareTotal = 0,
  convamount = 0,
  insuranceTotal = 0,
  discount = 0,
  tranxFree = 0,
  redeemTotal = 0,
}) => {
  const hotelFare = Number(hotelFareTotal) || 0;
  const extras =
    Number(convamount) +
    Number(insuranceTotal) +
    Number(tranxFree) -
    Number(discount) -
    Number(redeemTotal);

  return Number((hotelFare + extras).toFixed(2));
};

/** Pin first details-page room card to listing min price (matches HotelsList card). */
export const pinFirstOpenRoomToListingMin = (rooms = [], listingMin, options = {}) => {
  const min = Math.ceil(Number(listingMin) || 0);
  if (min <= 0 || !Array.isArray(rooms) || rooms.length === 0) {
    return rooms;
  }
  const first = rooms[0];
  const ratePlans = Array.isArray(first?.ratePlans) ? [...first.ratePlans] : [];
  if (!ratePlans[0]?.price) {
    return rooms;
  }
  const firstPlan = ratePlans[0];
  if (isRatePlanExplicitlyPanOptional(firstPlan, first)) {
    return rooms;
  }
  const price = { ...ratePlans[0].price };
  const tax = Math.max(0, Number(price.tax ?? price.Tax ?? 0));
  const grossSource =
    price.tripjackSupplierGross ??
    (price.tripjackGrossLocked === true ? price.supplierGross : 0);
  const gross = Math.ceil(Number(grossSource) || 0);
  if (gross > 0) {
    price.supplierGross = gross;
    price.tripjackSupplierGross = gross;
    price.tripjackGrossLocked = true;
  }
  price.total = min;
  price.Total = min;
  price.offerPrice = min;
  // Listing card price already includes Admin markup. Do not add it again.
  price.adminHotelMarkupApplied = true;
  price.base = tax > 0 && min > tax ? min - tax : min;
  price.tax = tax > 0 && min > tax ? tax : 0;
  price.portalPriceApplied = true;
  price.customerDisplayPinned = true;
  const isGuest = Number(options?.roleType ?? 4) === 4;
  const markupRate = isMasterMemberAgent(options?.roleType, options?.membership)
    ? MASTER_MEMBER_MARKUP_RATE
    : isGuest
      ? HOTEL_GUEST_LISTING_MARKUP_RATE
      : 0;
  price.portalMarkupRate = markupRate > 0 ? markupRate : undefined;
  price.portalDiscountRate = markupRate > 0 ? 0 : Number(price.portalDiscountRate) || 0;
  ratePlans[0] = { ...ratePlans[0], price };
  return [{ ...first, ratePlans }, ...rooms.slice(1)];
};

/** Pin checkout fare to hotel-details roomPlan.displayTotal (authoritative for Final Payment). */
export const applyCheckoutDisplayTotalsToHotels = (hotels, roomPlan = []) => {
  if (!hotels?.rooms || !Array.isArray(roomPlan) || roomPlan.length === 0) {
    return hotels;
  }
  const rooms = hotels.rooms.map((room, index) => {
    const plan = roomPlan[index] ?? roomPlan[0];
    const displayTotal = Math.ceil(Number(plan?.displayTotal ?? plan?.DisplayTotal ?? 0));
    if (displayTotal <= 0) {
      return room;
    }
    const ratePlans = Array.isArray(room.ratePlans) ? [...room.ratePlans] : [];
    if (!ratePlans[0]?.price) {
      return room;
    }
    const planRateId = String(plan?.rateID ?? plan?.rateId ?? '').trim();
    const returnedRateId = String(ratePlans[0]?.ratePlanId ?? '').trim();
    // When hotelprice returns another hotel's session (shared traceId), rate ids differ —
    // still pin the fare the user selected on hotel-details.
    if (
      planRateId &&
      returnedRateId &&
      planRateId !== returnedRateId &&
      displayTotal <= 0
    ) {
      return room;
    }
    const price = { ...ratePlans[0].price };
    const tax = Math.max(0, Number(price.tax ?? price.Tax ?? 0));
    price.total = displayTotal;
    price.Total = displayTotal;
    price.offerPrice = displayTotal;
    price.base = tax > 0 && displayTotal > tax ? displayTotal - tax : displayTotal;
    price.tax = tax > 0 && displayTotal > tax ? tax : 0;
    price.customerDisplayPinned = true;
    ratePlans[0] = { ...ratePlans[0], price };
    return { ...room, ratePlans };
  });
  return { ...hotels, rooms };
};
